// ./src/form/PhoneField.tsx
//
// Phone input on the shared `(name, value)` change contract. A country dropdown
// (flag + calling code) sets the parse region; the visible text is formatted
// live as the user types; the EMITTED value is canonical E.164 when the number
// is usable, else the raw input kept as-is (so a flagged/partial number is never
// silently dropped — same "normalize, don't reject" rule as the API).
//
// Mantine-native (no react-phone-number-input CSS to import) so it matches every
// other field in the app. Region list + normalization come from @em/utils, which
// wraps libphonenumber-js — the single source of truth shared with the backend.

import React from 'react';
import { TextInput, TextInputProps, Menu, UnstyledButton, Group, Text, ScrollArea } from '@mantine/core';
import {
  PHONE_COUNTRIES,
  type PhoneCountryCode,
  normalizePhone,
  formatPhoneAsYouType,
  detectPhoneRegion,
  isValidPhone,
} from '@em/utils';

import { useAppEnvironment } from '../AppEnvironmentContext';

import { FieldChangeHandler } from './types';
import { OptionPickerModal } from './OptionPickerModal';
import { usesPhonePicker } from './pickerRules';

export interface PhoneFieldProps extends Omit<TextInputProps, 'onChange' | 'value' | 'leftSection'> {
  name?: string;
  /** Stored value — canonical E.164 (`+15877142557`) or a raw/flagged string. */
  value?: string | null;
  onChange?: FieldChangeHandler<string>;
  /** Default parse region for local numbers (ISO alpha-2). Usually the brand's `space.default_country`. */
  defaultCountry?: PhoneCountryCode | string;
}

const COUNTRY_SET = new Set(PHONE_COUNTRIES.map((c) => c.code));

const asCountry = (cc?: PhoneCountryCode | string): PhoneCountryCode => {
  const up = String(cc || 'US').toUpperCase() as PhoneCountryCode;
  return COUNTRY_SET.has(up) ? up : 'US';
};

// forwardRef so a drawer/modal can autofocus this as its first field.
export const PhoneField = React.forwardRef<HTMLInputElement, PhoneFieldProps>(
  ({ name, value, onChange, defaultCountry, disabled, error, ...rest }, ref) => {
    const fallback = asCountry(defaultCountry);
    const { isMobile } = useAppEnvironment();
    // ⚠ The country list is a DROPDOWN, so on a phone it is the full-screen picker like every other (owner, 2026-10-09):
    //   a 280px floating list of ~240 countries under the thumb, inside a bottom sheet, was the hardest picker to use.
    const phonePicker = usesPhonePicker({ isMobile, disabled });
    const [pickerOpen, setPickerOpen] = React.useState(false);

    // Country is seeded from an existing value's own country (E.164 self-describes),
    // else the brand default. Kept in state so the user can switch it.
    const [country, setCountry] = React.useState<PhoneCountryCode>(() =>
      value ? detectPhoneRegion(value, fallback) : fallback,
    );
    const [text, setText] = React.useState<string>(() => (value ? formatPhoneAsYouType(String(value), country) : ''));

    // Re-sync the visible text + country when the STORED value changes externally
    // (form load / reset) — but not from our own emits: if what we'd emit already
    // equals the incoming value, the change came from this field, so leave the
    // in-progress text alone (avoids clobbering the caret / a trailing separator).
    React.useEffect(() => {
      const incoming = value ?? '';
      const emitted = normalizePhone(text, country).value ?? '';
      if (incoming === emitted) {
        return;
      }

      const nextCountry = incoming ? detectPhoneRegion(incoming, fallback) : fallback;
      setCountry(nextCountry);
      setText(incoming ? formatPhoneAsYouType(String(incoming), nextCountry) : '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const emit = (rawText: string, region: PhoneCountryCode) => {
      const norm = normalizePhone(rawText, region);
      onChange?.(name, norm.value ?? '');
    };

    const handleInput = (raw: string) => {
      const formatted = formatPhoneAsYouType(raw, country);
      setText(formatted);
      emit(formatted, country);
    };

    const handleCountry = (cc: PhoneCountryCode) => {
      setCountry(cc);
      // Re-parse the digits already entered under the newly chosen region.
      const formatted = formatPhoneAsYouType(text, cc);
      setText(formatted);
      emit(formatted, cc);
    };

    const selected = PHONE_COUNTRIES.find((c) => c.code === country) ?? PHONE_COUNTRIES[0];

    // A usable number gets no warning; a non-empty but unusable one is flagged softly
    // (kept, not blocked) unless the caller already supplied its own error.
    const softError =
      !error && text.trim() && !isValidPhone(text, country) ? 'This number looks incomplete.' : undefined;

    const countryButton = phonePicker ? (
      <>
        <UnstyledButton
          disabled={disabled}
          onClick={() => setPickerOpen(true)}
          aria-label="Select country"
          style={{ paddingInline: 8, height: '100%', display: 'flex', alignItems: 'center' }}
        >
          <Group gap={4} wrap="nowrap">
            <Text component="span" fz="lg" lh={1}>
              {selected.flag}
            </Text>
            <Text component="span" c="dimmed" fz="sm">
              {selected.callingCode}
            </Text>
          </Group>
        </UnstyledButton>
        <OptionPickerModal
          opened={pickerOpen}
          onClose={() => setPickerOpen(false)}
          title="Country"
          searchPlaceholder="Search countries"
          // The label is what the search matches (a name or a calling code); the row draws the parts from the item.
          data={PHONE_COUNTRIES.map((c) => ({ value: c.code, label: `${c.name} ${c.callingCode}`, flag: c.flag, name: c.name, callingCode: c.callingCode }))}
          value={country}
          onSelect={(v) => v && handleCountry(v as PhoneCountryCode)}
          renderOption={({ option }) => (
            <Group gap="sm" wrap="nowrap">
              <Text component="span" fz="lg" lh={1}>
                {String(option.flag ?? '')}
              </Text>
              <Text component="span" fz="md" style={{ flex: 1, minWidth: 0 }}>
                {String(option.name ?? option.label)}
              </Text>
              <Text component="span" c="dimmed" fz="md">
                {String(option.callingCode ?? '')}
              </Text>
            </Group>
          )}
        />
      </>
    ) : (
      <Menu position="bottom-start" withinPortal shadow="md" width={280} disabled={disabled}>
        <Menu.Target>
          <UnstyledButton
            disabled={disabled}
            aria-label="Select country"
            style={{ paddingInline: 8, height: '100%', display: 'flex', alignItems: 'center' }}
          >
            <Group gap={4} wrap="nowrap">
              <Text component="span" fz="lg" lh={1}>
                {selected.flag}
              </Text>
              <Text component="span" c="dimmed" fz="sm">
                {selected.callingCode}
              </Text>
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <ScrollArea.Autosize mah={280} type="scroll">
            {PHONE_COUNTRIES.map((c) => (
              <Menu.Item
                key={c.code}
                onClick={() => handleCountry(c.code)}
                leftSection={
                  <Text component="span" fz="lg" lh={1}>
                    {c.flag}
                  </Text>
                }
                rightSection={
                  <Text component="span" c="dimmed" fz="sm">
                    {c.callingCode}
                  </Text>
                }
              >
                {c.name}
              </Menu.Item>
            ))}
          </ScrollArea.Autosize>
        </Menu.Dropdown>
      </Menu>
    );

    return (
      <TextInput
        ref={ref}
        name={name}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={text}
        disabled={disabled}
        onChange={(e) => handleInput(e.currentTarget.value)}
        leftSection={countryButton}
        leftSectionWidth={72}
        leftSectionProps={{ style: { pointerEvents: 'auto' } }}
        error={error ?? softError}
        {...rest}
      />
    );
  },
);

PhoneField.displayName = 'PhoneField';
